import { useAuth } from '@common/auth-layer/composables/useAuth'
import { resolveSessionIdFromAuthResponse } from '@common/auth-layer/utils/session.utils'
import { useCurrentUser } from '@common/current-user-layer/composables/useCurrentUser'

import { useLogger } from '@core/composables/useLogger'

/**
 * Auth initialization plugin - shared across all apps
 * Runs early to set up authentication state
 */
export default defineNuxtPlugin(async nuxtApp => {
  const { logger } = useLogger()

  try {
    logger.info('[Init-Auth] Starting auth initialization')

    // On server side, check if auth data was extracted by middleware
    if (process.server && nuxtApp.ssrContext?.event?.context?.auth) {
      const authData = nuxtApp.ssrContext.event.context.auth
      logger.info('[Init-Auth] Found auth data from server middleware')

      // Set auth in store directly
      const { registerAuth } = useAuth()
      const sessionUniqueKey = resolveSessionIdFromAuthResponse(authData)
      registerAuth(authData.token, authData.userId, authData.contactId, sessionUniqueKey)

      // Load current user data
      const { ensureLoaded } = useCurrentUser()
      try {
        await ensureLoaded()
      } catch (err) {
        logger.warn('[Init-Auth] Server-side current-user load failed', {
          error: err instanceof Error ? err : new Error(String(err)),
        })
      }
    } else {
      // Client-side or no server auth: use the existing checkAuth flow
      const { checkAuth } = useAuth()
      const authResult = await checkAuth()

      if (authResult.success) {
        // Load current user data (profile, groups, courses) if authenticated
        const { ensureLoaded } = useCurrentUser()
        try {
          await ensureLoaded()
        } catch (err) {
          // Current-user is optional at boot; ignore failures
          logger.warn('[Init-Auth] Current-user load failed', {
            error: err instanceof Error ? err : new Error(String(err)),
          })
        }
      }
    }
  } catch (error) {
    // Never block app start due to bootstrap; log for diagnostics
    logger.warn('[Init-Auth] Auth initialization failed', {
      error: error instanceof Error ? error : new Error(String(error)),
    })
  }
})
